import type {
  Metafield,
  Metaobject,
  MetaobjectConnection,
} from "@shopify/hydrogen-react/storefront-api-types";
import { query } from "gql-query-builder";
import {
  collectionFragment,
  mediaImageFragment,
  metaObjectFragment,
  metaObjectImageProductFragment,
  metaObjectProductFragment,
  productFragment,
  productVariantFragment,
  variantMetafields,
  videoFragment,
} from "./fragments";
import { APP_NAME, productFields, variantFields } from "./fields";
import { variants } from "./product-gql";

export interface IMetaobjectQuery {
  metaobjects: MetaobjectConnection;
}

export const metaobjectQuery = query(
  {
    operation: "metaobjects",
    fields: [
      {
        edges: [
          {
            node: [
              "handle",
              "id",
              {
                operation: "fields",
                fields: ["value", "key", "type"],
                variables: {},
              },
            ],
          },
        ],
      },
    ],
    variables: {
      type: { type: "String!" },
      first: { type: "Int" },
    },
  },
  undefined,
  { operationName: "Metaobjects" },
);

/**
 * Media 메타오브젝트 조회
 */
export interface IMediaMetaObjectQuery {
  metaobjects: MetaobjectConnection;
}

export const mediaMetaobjectQuery = query(
  {
    operation: "metaobjects",
    fields: [
      {
        edges: [
          {
            node: [
              "handle",
              "id",
              "updatedAt",
              {
                operation: "fields",
                fields: [
                  "value",
                  "key",
                  "type",
                  {
                    operation: "references(first: 100)",
                    fields: [{ edges: [{ node: [mediaImageFragment] }] }],
                    variables: {},
                  },
                  {
                    operation: "reference",
                    fields: [mediaImageFragment],
                    variables: {},
                  },
                ],

                variables: {},
              },
            ],
          },
        ],
      },
    ],
    variables: {
      type: { type: "String!" },
      first: { type: "Int" },
      reverse: { type: "Boolean" },
      sortKey: { type: "String" },
    },
  },
  undefined,
  { operationName: "MediaMetaobjects" },
);

/**
 * 상품 검색 Metaobject 조회
 */
const searchMetaobjectField: Record<string, string> = {
  tune: 'value: field(key: "value")',
  yeseyesee: 'value: field(key: "values")',
  thisisneverthat: 'value: field(key: "values")',
  khakis: 'value: field(key: "values")',
  sunlove: 'value: field(key: "values")',
  wellbeingexpress: 'value: field(key: "values")',
};

export interface ISearchMetaObjectsQuery {
  metaobjects: {
    edges: {
      node: Metaobject & { value?: Metafield };
    }[];
  };
}

export const searchMetaobjectQuery = query(
  {
    operation: "metaobjects",
    fields: [
      {
        edges: [
          {
            node: [
              "handle",
              {
                operation: searchMetaobjectField[APP_NAME],
                fields: ["value", "type"],
                variables: {},
              },
            ],
          },
        ],
      },
    ],
    variables: {
      type: { type: "String!" },
      first: { type: "Int" },
      reverse: { type: "Boolean" },
    },
  },

  undefined,
  { operationName: "MetaobjectsSearch" },
);

/**
 * 메타오브젝트 handle 조회
 */
export interface IMetaobjectByHandleQuery {
  metaobject: Metaobject;
}

export const metaobjectByHandleQuery = query(
  {
    operation: "metaobject",
    fields: [
      "handle",
      "id",
      "updatedAt",
      {
        operation: "fields",
        fields: [
          "value",
          "key",
          "type",
          {
            operation: "references(first: 100)",
            fields: [
              {
                edges: [
                  {
                    node: [
                      mediaImageFragment,
                      collectionFragment,
                      metaObjectFragment,
                    ],
                  },
                ],
              },
            ],
            variables: {},
          },
          {
            operation: "reference",
            fields: [mediaImageFragment, metaObjectFragment],
            variables: {},
          },
        ],
        variables: {},
      },
    ],
    variables: { handle: { type: "MetaobjectHandleInput!" } },
  },
  undefined,
  { operationName: "Metaobject" },
);

/**
 * Archive 메타오브젝트 조회
 */
export interface IIndexMetaObjectQuery {
  metaobjects: MetaobjectConnection;
}

export const indexMetaobjectQuery = query({
  operation: "metaobjects",
  fields: [
    {
      edges: [
        {
          node: [
            "handle",
            "id",
            "updatedAt",
            {
              operation: "fields",
              fields: [
                "value",
                "key",
                "type",
                {
                  operation: "references(first: 48)",
                  fields: [
                    {
                      edges: [
                        {
                          node: [
                            productFragment,
                            mediaImageFragment,
                            metaObjectImageProductFragment,
                          ],
                        },
                      ],
                    },
                  ],
                  variables: {},
                },
                {
                  operation: "reference",
                  fields: [metaObjectImageProductFragment, mediaImageFragment],
                  variables: {},
                },
              ],
              variables: {},
            },
          ],
        },
      ],
    },
  ],
  variables: {
    type: { type: "String!" },
    first: { type: "Int" },
    reverse: { type: "Boolean" },
  },
});

/**
 * type으로 메타오브젝트 조회
 */
export interface IMetaObjectTypeQuery {
  metaobjects: MetaobjectConnection;
}

export const metaobjectTypeQuery = query(
  {
    operation: "metaobjects",
    fields: [
      {
        edges: [
          {
            node: [
              "id",
              "handle",
              {
                operation: "fields",
                fields: [
                  "value",
                  "key",
                  "type",
                  {
                    operation: "references(first: 12)",
                    fields: [
                      {
                        edges: [
                          {
                            node: [
                              {
                                operation: "Product",
                                fields: [
                                  ...productFields,
                                  {
                                    operation: "variants(first: 10)",
                                    fields: [
                                      {
                                        edges: [
                                          {
                                            node: [
                                              { product: ["handle"] },
                                              ...variantFields,
                                              ...variantMetafields[APP_NAME],
                                            ],
                                          },
                                        ],
                                      },
                                    ],
                                    variables: {},
                                  },
                                ],
                                fragment: true,
                              },
                              {
                                operation: "MediaImage",
                                fields: ["id", mediaImageFragment],
                                fragment: true,
                              },
                            ],
                          },
                        ],
                      },
                    ],
                    variables: {},
                  },
                  {
                    operation: "reference",
                    fields: [
                      mediaImageFragment,
                      videoFragment,
                      collectionFragment,
                      productVariantFragment,
                      metaObjectImageProductFragment,
                      {
                        operation: "Product",
                        fields: [...productFields, variants(40)],
                        fragment: true,
                      },
                    ],
                    variables: {},
                  },
                  {
                    operation: "references(first: 12)",
                    fields: [
                      {
                        edges: [
                          {
                            node: [
                              metaObjectFragment,
                              mediaImageFragment,
                              videoFragment,
                            ],
                          },
                        ],
                      },
                    ],
                    variables: {},
                  },
                ],
                variables: {},
              },
            ],
          },
        ],
      },
    ],
    variables: {
      type: { type: "String!" },
      first: { type: "Int" },
      reverse: { type: "Boolean" },
    },
  },
  undefined,
  { operationName: "Metaobjects" },
);

export interface IRankingObjectTypeQuery {
  metaobjects: MetaobjectConnection;
}

export const rankingMetaobjectQuery = query({
  operation: "metaobjects",
  fields: [
    {
      edges: [
        { node: ["handle", "id", "updatedAt", metaObjectProductFragment] },
      ],
    },
  ],
  variables: {
    type: { type: "String!" },
    first: { type: "Int" },
    reverse: { type: "Boolean" },
  },
});

/**
 * Archive 메타오브젝트 조회
 */
export interface IMetaobjectImageQuery {
  metaobjects: MetaobjectConnection;
}

export const metaobjectImageQuery = query(
  {
    operation: "metaobjects",
    fields: [
      {
        edges: [
          {
            node: [
              "handle",
              "id",
              "updatedAt",
              {
                operation: "fields",
                fields: [
                  "value",
                  "key",
                  "type",
                  {
                    operation: "references(first: 100)",
                    fields: [
                      {
                        edges: [
                          {
                            node: [
                              {
                                operation: "Metaobject",
                                fields: [
                                  "id",
                                  {
                                    fields: [
                                      "key",
                                      "value",
                                      "type",
                                      {
                                        operation: "references(first: 100)",
                                        fields: [
                                          {
                                            edges: [
                                              { node: [metaObjectFragment] },
                                            ],
                                          },
                                        ],
                                        variables: {},
                                      },
                                    ],
                                  },
                                ],
                                fragment: true,
                              },
                            ],
                          },
                        ],
                      },
                    ],
                    variables: {},
                  },
                  {
                    operation: "reference",
                    fields: [mediaImageFragment, metaObjectFragment],
                    variables: {},
                  },
                ],
                variables: {},
              },
            ],
          },
        ],
      },
    ],
    variables: {
      type: { type: "String!" },
      first: { type: "Int" },
      reverse: { type: "Boolean" },
    },
  },
  undefined,
  { operationName: "MetaobjectImage" },
);
